import { FormContextType, RJSFSchema, StrictRJSFSchema, WidgetProps } from '@rjsf/utils';
import { useDebounceEffect } from 'ahooks';
import { cloneElement, memo, ReactElement, useEffect, useRef, useState } from 'react';

function Wrapper<T = unknown, S extends StrictRJSFSchema = RJSFSchema, F extends FormContextType = unknown>(
	props: Pick<WidgetProps<T, S, F>, 'value' | 'onChange'> & { children?: ReactElement }
) {
	const [currentValue, setCurrentValue] = useState<unknown>(props.value);
	const propSyncValueRef = useRef<unknown>();
	const hasPendingPropSyncRef = useRef(false);

	useDebounceEffect(
		() => {
			if (hasPendingPropSyncRef.current && Object.is(propSyncValueRef.current, currentValue)) {
				hasPendingPropSyncRef.current = false;
				propSyncValueRef.current = undefined;
				return;
			}

			hasPendingPropSyncRef.current = false;
			propSyncValueRef.current = undefined;
			props.onChange(currentValue);
		},
		[currentValue],
		{
			wait: 200,
		}
	);

	useEffect(() => {
		const nextValue = props.value as unknown;

		setCurrentValue((previousValue: unknown): unknown => {
			if (Object.is(previousValue, nextValue)) {
				return previousValue;
			}

			hasPendingPropSyncRef.current = true;
			propSyncValueRef.current = nextValue;

			return nextValue;
		});
	}, [props.value]);

	return cloneElement(props.children, { onChange: setCurrentValue, value: currentValue }, []);
}

const MemoizedWrapper = memo(Wrapper);

MemoizedWrapper.displayName = 'Wrapper';

export { MemoizedWrapper as Wrapper };
